import React from 'react'
import { Link, Stack } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { getSystemIcon } from '../../SystemIcons'
import {
  handleLightspeedLinkClick,
  handleXeroLinkClick,
  handleExternalLinkClick,
} from '../../../utils/externalLinks'
import type { SourceLink } from '../../../config/exceptions/shared/issuesState'
import { poolwerxColors } from '../../../theme/colors'

/**
 * The drawer's source deep-link row: "View Lightspeed", "View Xero",
 * "View Skimmer" (sale domain; products still say "Open in Lightspeed") -
 * rendered generically from ``row.source_links``
 * (backend ``build_source_links``) directly under the object identity subtitle.
 * Hidden when there are no links.
 *
 * Each link is a real ``<Link href>`` so right-click / ctrl+click "open in new
 * tab" work natively (RULES.md -> Navigation); the left-click handler opens the
 * vendor popup. System-specific handlers (Lightspeed / Xero) size the popup;
 * anything else falls back to the generic external handler.
 */
const handlerFor = (system: string) => {
  const key = system.toLowerCase()
  if (key === 'lightspeed') return handleLightspeedLinkClick
  if (key === 'xero') return handleXeroLinkClick
  return handleExternalLinkClick
}

// The brand icon already names the system, so the verb is noise: drop a leading
// "View " / "Open in " so the label reads just "Lightspeed" / "Xero" / "Skimmer".
// Display-only - the backend label is kept in the link's title for a11y.
const shortLabel = (label: string): string =>
  label.replace(/^(view|open in)\s+/i, '')

interface SourceLinksRowProps {
  links?: SourceLink[]
  /**
   * Optional in-flow action(s) rendered after the links (e.g. the drawer's
   * "Compare sales" dialog trigger). Shares the same wrapping row so the
   * cluster wraps as one unit when space runs out.
   */
  trailing?: React.ReactNode
}

const SourceLinksRow: React.FC<SourceLinksRowProps> = ({ links, trailing }) => {
  if ((!links || links.length === 0) && !trailing) return null

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      spacing={2.5}
      sx={{ mt: '14px' }}
    >
      {(links ?? []).map((link) => {
        // Brand icon leads (when the system has one); the generic external-link
        // glyph trails the text so each link reads "<icon> View X ↗".
        const systemIcon = getSystemIcon(link.system, { size: 15 })
        return (
          <Link
            key={`${link.system}-${link.kind ?? ''}-${link.url}`}
            href={link.url}
            title={link.label}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
              handlerFor(link.system)(e, link.url, link.label)
            }
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '12.5px',
              fontWeight: 500,
              color: poolwerxColors.teal,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {systemIcon}
            {shortLabel(link.label)}
            <OpenInNewIcon sx={{ fontSize: 12 }} />
          </Link>
        )
      })}
      {trailing}
    </Stack>
  )
}

export default SourceLinksRow
